import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { DsBadge } from "../../../../design-system";
import {
  formatHistoryStatus,
  historyStatusTone,
  loadEssRequestHistory,
} from "../../../../lib/essRequestHistory";

export const DS_QUICK_FIELD_CLS =
  "mt-1.5 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm text-[hsl(var(--ds-fg))] outline-none transition focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-primary))]/20";

export function useEssRequestHistory({ enabled, actionLabel, employeeId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setOpen(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadEssRequestHistory({ actionLabel, employeeId })
      .then((list) => {
        if (!cancelled) setRows(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, actionLabel, employeeId]);

  return { open, setOpen, rows, loading };
}

export function RequestFormFooter({
  historyCount = 0,
  historyLoading = false,
  onViewHistory,
  onCancel,
  submitDisabled = false,
  isSubmitting = false,
  submitLabel = "Submit Request",
  cancelDisabled = false,
}) {
  return (
    <div className="flex min-h-[40px] flex-wrap items-center gap-x-3 gap-y-2 border-t border-[hsl(var(--ds-border))] pt-4">
      <div className="flex min-w-[133px] flex-1 items-center justify-start">
        <button
          type="button"
          onClick={onViewHistory}
          className="inline-flex h-9 items-center justify-center rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-4 text-xs font-medium text-[hsl(var(--ds-fg))] transition hover:opacity-90"
        >
          View history
          {!historyLoading && historyCount > 0 ? ` (${historyCount})` : ""}
        </button>
      </div>
      <div className="flex min-w-[200px] flex-1 justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={cancelDisabled || isSubmitting}
          className="inline-flex h-9 items-center justify-center rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-4 text-xs font-medium text-[hsl(var(--ds-fg))] transition hover:bg-[hsl(var(--ds-muted))] disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitDisabled || isSubmitting}
          className="inline-flex h-9 items-center justify-center rounded-xl border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary))] px-4 text-xs font-medium text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? "Submitting…" : submitLabel}
        </button>
      </div>
    </div>
  );
}

export function RequestHistoryOverlay({
  open,
  onClose,
  title,
  subtitle,
  rows = [],
  loading = false,
  emptyMessage,
}) {
  if (!open) return null;
  const overlay = (
    <div
      className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="flex max-h-[min(76vh,532px)] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-card)]"
        role="dialog"
        aria-labelledby="ess-request-history-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div>
            <h3
              id="ess-request-history-title"
              className="text-base font-semibold text-[hsl(var(--ds-fg))]"
            >
              {title}
            </h3>
            {subtitle ? (
              <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-1.5 text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-muted))]"
            aria-label="Close history"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {loading ? (
            <p className="text-sm text-[hsl(var(--ds-muted-fg))]">Loading requests…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
              {emptyMessage || "No applications yet. Submit a request to add an entry here."}
            </p>
          ) : (
            <ul className="divide-y divide-[hsl(var(--ds-border))] rounded-xl border border-[hsl(var(--ds-border))]">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className="flex items-center gap-x-3 px-3 py-2.5 text-xs tabular-nums"
                >
                  <span className="min-w-0 shrink truncate font-semibold text-[hsl(var(--ds-fg))]">
                    {row.title}
                  </span>
                  {(row.details || []).map((d) => (
                    <span key={`${row.id}-${d.label}`} className="shrink-0">
                      <span className="font-medium text-[hsl(var(--ds-muted-fg))]">
                        {d.label}
                      </span>{" "}
                      {d.value}
                    </span>
                  ))}
                  <DsBadge
                    tone={historyStatusTone(row.status)}
                    className="ml-auto shrink-0 text-[10px]"
                  >
                    {formatHistoryStatus(row.status)}
                  </DsBadge>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
  if (typeof document === "undefined") return overlay;
  return createPortal(overlay, document.body);
}
